import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "components/ui/dropdown-menu";
import { cn } from "lib/utils";
import { Clock } from "lucide-react";
import { useEffect, useState } from "react";

const hours = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
const periods = ["AM", "PM"];

export default function TimePicker({
  value,
  className,
  onChange,
  placeholder = "Pick a time",
  disabled = false,
}: {
  value: string; // in 24h format: "18:30"
  className?: string;
  onChange: (value: string) => void; // will emit in 24h: "18:30"
  placeholder?: string;
  disabled?: boolean;
}) {
  const [manualTime, setManualTime] = useState(value || "18:30");
  const [selectedHour, setSelectedHour] = useState("06");
  const [selectedMinute, setSelectedMinute] = useState("30");
  const [period, setPeriod] = useState("PM");

  // Convert 24h -> internal 12h display
  const convert24to12 = (val: string) => {
    const [h, m] = val.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return {
      hour: String(hour12).padStart(2, "0"),
      minute: String(m).padStart(2, "0"),
      ampm,
    };
  };

  // Convert internal 12h -> 24h output
  const convertTo24 = (h: string, m: string, ampm: string) => {
    let hour = parseInt(h);
    if (ampm === "PM" && hour !== 12) hour += 12;
    if (ampm === "AM" && hour === 12) hour = 0;
    return `${String(hour).padStart(2, "0")}:${m}`;
  };

  // Sync with external 24h value
  useEffect(() => {
    setManualTime(value);
    const { hour, minute, ampm } = convert24to12(value);
    setSelectedHour(hour);
    setSelectedMinute(minute);
    setPeriod(ampm);
  }, [value]);

  const emit = (h: string, m: string, p: string) => {
    const formatted = convertTo24(h, m, p);
    setManualTime(formatted);
    onChange(formatted);
  };

  const handleManualChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value; // assume "18:30"
    setManualTime(val);
    onChange(val);
  };

  const handleDropdownSelect = (h: string, m: string, p: string) => {
    setSelectedHour(h);
    setSelectedMinute(m);
    setPeriod(p);
    emit(h, m, p);
  };

  return (
    <div className={cn("relative w-full", className)}>
      <>
        <input
          type="time"
          value={manualTime}
          onChange={handleManualChange}
          placeholder={placeholder}
          disabled={disabled}
          // py-[11px]
          className="w-full pr-10 rounded-md border border-input bg-background px-4 py-[9px] text-[15px] text-foreground shadow-sm 
               focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
        />

        <style>
          {`
      input::-webkit-calendar-picker-indicator {
        display: none;
      }
    `}
        </style>
      </>

      {/* Clock Icon Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" disabled={disabled} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground">
            <Clock className="w-5 h-5" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="flex space-x-2 p-2 w-auto bg-white dark:bg-neutral-900 rounded-xl shadow-xl" sideOffset={8}>
          {/* Hours */}
          <div className="h-48 w-14 overflow-y-auto">
            {hours.map((h) => (
              <div
                key={h}
                onClick={() => handleDropdownSelect(h, selectedMinute, period)}
                className={cn(
                  "cursor-pointer px-2 py-1 text-center rounded-md text-sm",
                  selectedHour === h ? "bg-blue-500 text-white font-semibold" : "hover:bg-muted hover:text-foreground"
                )}
              >
                {h}
              </div>
            ))}
          </div>

          {/* Minutes */}
          <div className="h-48 w-14 overflow-y-auto">
            {minutes.map((m) => (
              <div
                key={m}
                onClick={() => handleDropdownSelect(selectedHour, m, period)}
                className={cn(
                  "cursor-pointer px-2 py-1 text-center rounded-md text-sm",
                  selectedMinute === m ? "bg-blue-500 text-white font-semibold" : "hover:bg-muted hover:text-foreground"
                )}
              >
                {m}
              </div>
            ))}
          </div>

          {/* AM/PM */}
          <div className="h-48 w-14 overflow-y-auto">
            {periods.map((p) => (
              <div
                key={p}
                onClick={() => handleDropdownSelect(selectedHour, selectedMinute, p)}
                className={cn(
                  "cursor-pointer px-2 py-1 text-center rounded-md text-sm",
                  period === p ? "bg-blue-500 text-white font-semibold" : "hover:bg-muted hover:text-foreground"
                )}
              >
                {p}
              </div>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

